import { all, put, call, select, getContext, takeLatest } from 'redux-saga/effects';

import { Logger } from 'utils/Logger';
import { Analytics } from 'utils/Analytics';

import {
  ActivityOptionToAction,
  ActivityOptionToAnalyticsEvent,
} from 'enums/activity-feedback-type';
import { ActivityStatus, ActivityStatusLabel } from 'enums/activity-status';
import { ActivityType, ActivityTypeLabel } from 'enums/activity-type';
import { AnalyticsEventCategory, AnalyticsEventAction } from 'enums/analytics-types';

import {
  fundingSearchActivities,
  dismissActivity,
  triggerActivityAction,
  completeActivity,
  uploadDocuments,
  removeDocument,
  collectiveActivities,
} from './types';

import {
  requestFundingSearchActivitiesSuccess,
  requestFundingSearchActivitiesFailure,
  requestTriggerActivityActionSuccess,
  requestTriggerActivityActionFailure,
  requestDismissActivitySuccess,
  requestDismissActivityFailure,
  requestCompleteActivitySuccess,
  requestCompleteActivityFailure,
  requestUploadDocumentsSuccess,
  requestRemoveDocumentSuccess,
  requestCollectiveActivitiesSuccess,
  requestCollectiveActivitiesFailure,
} from './reducer';

import { requestFundingSearch } from '../fundingSearch/reducer';
import { makeSelectMasquerade } from '../auth/selectors';
import {
  requestUploadFundingDocumentSaga,
  requestRemoveFundingDocumentSaga,
} from '../documents/sagas';

const logger = new Logger('Saga:Activities');

export function* requestFundingSearchActivitiesSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.activities.funding_search_activities', {
        fundingSearchId: action.fundingSearchId,
      }),
    );

    yield put(requestFundingSearchActivitiesSuccess(response.data));
  } catch (error) {
    logger.error('Failed to request funding searches activities with the following error', error);
    yield put(requestFundingSearchActivitiesFailure(error));
  }
}

export function* requestDismissActivitySaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.patch,
      routerService.generate('api.activities.funding_search_dismiss_activity', {
        activityId: action.activityId,
      }),
    );

    yield put(requestDismissActivitySuccess(action.activityId, response.data));

    Analytics.track(
      AnalyticsEventCategory.fundingSearchTimeline,
      AnalyticsEventAction.activityDismiss,
      {
        extraParams: {
          fundingSearchId: action.fundingSearchId,
          activityId: action.activityId,
          activityType: ActivityTypeLabel[response.data.type],
          activityState: ActivityStatusLabel[response.data.status],
        },
      },
    );
  } catch (error) {
    logger.error('Failed to request dismiss activity with the following error', error);
    yield put(requestDismissActivityFailure(action.activityId, error));
  }
}

export function* requestTriggerActivityActionSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.patch,
      routerService.generate('api.activities.activity_action', {
        action: ActivityOptionToAction[action.feedbackType],
        activityId: action.activityId,
      }),
    );

    yield put(requestTriggerActivityActionSuccess(action.activityId, response.data));
    action.promise && action.promise.resolve(response.data);

    Analytics.track(
      AnalyticsEventCategory.fundingSearchTimeline,
      ActivityOptionToAnalyticsEvent[action.feedbackType],
      {
        extraParams: {
          activityId: action.activityId,
          activityType: ActivityTypeLabel[ActivityType.feedback],
          activityState: ActivityStatusLabel[ActivityStatus.due],
        },
      },
    );

    if (response.data.status === ActivityStatus.completed) {
      Analytics.track(
        AnalyticsEventCategory.fundingSearchTimeline,
        AnalyticsEventAction.completed,
        {
          extraParams: {
            fundingSearchId: action.fundingSearchId,
            activityId: action.activityId,
            activityType: ActivityTypeLabel[ActivityType.feedback],
            activityState: ActivityStatusLabel[ActivityStatus.completed],
          },
        },
      );
    }

    // After a successful activity action we re-fetch the funding search
    yield put(requestFundingSearch(action.fundingSearchId));
  } catch (error) {
    logger.error('Failed to trigger activity action with the following error', error);
    yield put(requestTriggerActivityActionFailure(error));
    action.promise && action.promise.reject(error);
  }
}

export function* requestCompleteActivitySaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.patch,
      routerService.generate('api.activities.complete_activity', { activityId: action.activityId }),
      { documents: action.documents },
    );

    yield put(requestCompleteActivitySuccess(action.activityId, response.data));
    action.promise && action.promise.resolve(response.data);

    const isMasquerading = yield select(makeSelectMasquerade());

    Analytics.track(
      AnalyticsEventCategory.fundingSearchTimeline,
      AnalyticsEventAction.documentSubmitted,
      {
        extraParams: {
          isMasquerading: !!isMasquerading,
          activityId: action.activityId,
          fundingSearchId: action.fundingSearchId,
          activityType: ActivityTypeLabel[ActivityType.documents],
          activityState: ActivityStatusLabel[ActivityStatus.due],
          ...action.metaData,
        },
      },
    );

    const metaData = {
      ...action.metaData,
      activityState: ActivityStatusLabel[ActivityStatus.completed],
    };

    Analytics.track(AnalyticsEventCategory.fundingSearchTimeline, AnalyticsEventAction.completed, {
      extraParams: {
        activityId: action.activityId,
        fundingSearchId: action.fundingSearchId,
        activityType: ActivityTypeLabel[ActivityType.documents],
        activityState: ActivityStatusLabel[ActivityStatus.completed],
        ...metaData,
      },
    });
  } catch (error) {
    logger.error('Failed to trigger activity action with the following error', error);
    yield put(requestCompleteActivityFailure(action.activityId, error));
    action.promise && action.promise.reject(error);
  }
}

export function* requestUploadDocumentsSaga(action) {
  try {
    const response = yield call(
      requestUploadFundingDocumentSaga,
      action.identifier,
      action.fundingSearchId,
      action.documentType,
      action.files,
    );

    yield put(requestUploadDocumentsSuccess(action.activityId, response.data.documents));

    const isMasquerading = yield select(makeSelectMasquerade());

    Analytics.track(
      AnalyticsEventCategory.fundingSearchTimeline,
      AnalyticsEventAction.documentUploaded,
      {
        extraParams: {
          isMasquerading,
          activityId: action.activityId,
          fundingSearchId: action.fundingSearchId,
          activityType: ActivityTypeLabel[ActivityType.documents],
          activityState: ActivityStatusLabel[ActivityStatus.due],
          ...action.metaData,
        },
      },
    );
  } catch (error) {
    logger.error('Failed to upload activity documents with the following error', error);
  }
}

export function* requestRemoveDocumentSaga(action) {
  try {
    yield call(requestRemoveFundingDocumentSaga, action.fundingSearchId, action.documentId);

    yield put(requestRemoveDocumentSuccess(action.activityId, action.documentId));
  } catch (error) {
    logger.error('Failed to remove funding search documents with the following error', error);
  }
}

export function* requestCollectiveActivitiesSaga() {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.activities.activities_grouped_by_funding_search', {
        filter: { where: { status: ActivityStatus.due } },
      }),
    );

    yield put(requestCollectiveActivitiesSuccess(response.data));
  } catch (error) {
    logger.error('Failed to remove document with the following error', error);
    yield put(requestCollectiveActivitiesFailure(error));
  }
}

export default function* activitiesSaga() {
  return yield all([
    takeLatest(fundingSearchActivities.request, requestFundingSearchActivitiesSaga),
    takeLatest(dismissActivity.request, requestDismissActivitySaga),
    takeLatest(triggerActivityAction.request, requestTriggerActivityActionSaga),
    takeLatest(completeActivity.request, requestCompleteActivitySaga),
    takeLatest(uploadDocuments.request, requestUploadDocumentsSaga),
    takeLatest(removeDocument.request, requestRemoveDocumentSaga),
    takeLatest(collectiveActivities.request, requestCollectiveActivitiesSaga),
  ]);
}
